import {
  // The package's barrel `.d.ts` re-exports `storeMetrics` from a module that has no
  // declaration, so it resolves to `any`. Alias + annotate to its real (no-arg, void)
  // signature so the typed lint rules see a safe call — this adds no runtime behaviour.
  storeMetrics as storeMetricsUntyped,
  clickIdMetrics,
  hubspotMetrics,
  googleTagLinker,
  decorateNow,
  INCLUDED_DOMAINS,
} from '@livechat/store-metrics';

const storeMetrics: () => void = storeMetricsUntyped;

const LINKER_OPTIONS = { allowedDomains: INCLUDED_DOMAINS } as const;

/**
 * Auth surfaces (`accounts.livechat.com`, `accounts.labs.livechat.com`,
 * `accounts.staging.livechat.com`, …) are never decorated. Forwarding `_gl` + ad-IDs
 * to accounts makes it reflect them back into the OAuth token callback fragment,
 * polluting the landing URL and placing `_gl` where GA cannot read it (fragment
 * instead of query). All accounts hosts share the `accounts.` subdomain label. AC-3347.
 */
const AUTH_HOST_PREFIX = 'accounts.';

let initialized = false;

/**
 * True when `url` resolves to a cross-domain destination whose registrable host is one
 * of the package's INCLUDED_DOMAINS (exact host or subdomain). Same-origin and
 * out-of-scope hosts return false so we never decorate them.
 *
 * This is a targeting gate only — it decides *whether* to decorate. All parameter
 * building/attaching is done by the package.
 */
export function isInScopeCrossDomain(url: string): boolean {
  try {
    const { hostname } = new URL(url, window.location.href);
    const host = hostname.toLowerCase();
    if (host === window.location.hostname.toLowerCase()) {
      return false;
    }

    // Never decorate auth surfaces — see AUTH_HOST_PREFIX.
    if (host.startsWith(AUTH_HOST_PREFIX)) {
      return false;
    }

    return INCLUDED_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

/**
 * Decorate a destination URL for a programmatic redirect (window.location / router).
 * Builds a detached anchor, lets the package attach `_gl` + ad-IDs + attribution, and
 * returns the decorated href. Returns the original url when the destination is not an
 * in-scope cross-domain target. No custom param code — the package owns decoration.
 */
export function decorateUrlForRedirect(url: string): string {
  if (typeof document === 'undefined' || !isInScopeCrossDomain(url)) {
    return url;
  }

  const anchor = document.createElement('a');
  anchor.href = url;
  decorateNow(anchor, { linker: LINKER_OPTIONS });

  return anchor.href;
}

function decorateElement(element: HTMLAnchorElement | HTMLFormElement): void {
  const target = element instanceof HTMLFormElement ? element.action : element.href;
  if (!target || !isInScopeCrossDomain(target)) {
    return;
  }

  decorateNow(element, { linker: LINKER_OPTIONS });
}

/**
 * Single source-side owner of `_gl` and ad-ID cross-domain forwarding on
 * my.livechatinc.com. Runs the package inbound pass, suppresses native GTM `_gl`
 * decoration, and attaches delegated decorators for clicks, form submits, and
 * SPA-rendered links. Idempotent and browser-guarded.
 */
export function initStoreMetrics(): void {
  if (initialized || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  // Inbound pass — capture URL params, persist, write _fbp cookie.
  storeMetrics();
  clickIdMetrics();
  hubspotMetrics();

  // Own `_gl` decoration — suppress GTM's native cross-domain decorator.
  googleTagLinker.init();

  // Set only after the inbound pass succeeds, so a throw above leaves `initialized`
  // false and a later call can retry rather than permanently no-op the session.
  initialized = true;

  /**
   * Signal readiness to the inline GTM bootstrap in views/index.ejs, which defers injecting
   * gtm.js until this fires (or a timeout elapses) so GTM never boots before this inbound pass
   * has captured params / suppressed native decorators — see @livechat/store-metrics'
   * "Recommended initialization order". AC-4320.
   */
  document.dispatchEvent(new Event('store-metrics:ready'));

  // Outbound — delegated on document so SPA-rendered links are covered too.
  // `mousedown` fires before navigation for primary, middle, and right-click
  // (open-in-new-tab); `keydown`/Enter covers keyboard and assistive-tech
  // activation, which never fires `mousedown`. The two are mutually exclusive
  // per activation, so a link is never decorated twice.
  const decorateAnchorFromEvent = (event: Event): void => {
    const anchor = (event.target as Element | null)?.closest?.('a');
    if (anchor) {
      decorateElement(anchor);
    }
  };

  document.addEventListener('mousedown', decorateAnchorFromEvent, true);

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Enter') {
        decorateAnchorFromEvent(event);
      }
    },
    true,
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) {
        decorateElement(event.target);
      }
    },
    true,
  );
}
